import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Collapse,
  Divider,
  RadioGroup,
  Radio
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  GitHub as GitHubIcon,
  Security as SecurityIcon,
  Tag as TagIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Link as LinkIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Upload as UploadIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { repositoryService } from '../../services/repositoryService';
import { isAdmin, isAuthenticated } from '../../services/authService';
import { authGet, authPost } from '../../services/apiService';

/**
 * RepositoriesView component for managing GitHub repositories
 * @returns {JSX.Element} RepositoriesView component
 */
const RepositoriesView = () => {
  const [repositories, setRepositories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRepo, setEditingRepo] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [protectionDialogOpen, setProtectionDialogOpen] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [expandedStreams, setExpandedStreams] = useState({});
  const [formData, setFormData] = useState({
    githubRepoName: '',
    sonarProjectName: '',
    sonarBranch: 'develop',
    type: 'modern',
    owner: '',
    stream: '',
    drill4JAppId: '',
    githubWorkflowFileName: 'create-tag-and-jar-for-release.yml',
    serviceName: '',
    em: '',
    ignore: false,
    // Deployment configuration
    deploymentConfig: {
      adoPipelineId: 2048,
      serviceName: '',
      deploymentRegion: 'US',
      location: 'Primary',
      backupRequired: 'No',
      deploymentWithAppconfig: 'Yes'
    }
  });
  const [snykVulnerabilityCounts, setSnykVulnerabilityCounts] = useState({});
  const [streams, setStreams] = useState([]);
  const [repositoryStreamMappings, setRepositoryStreamMappings] = useState({});
  const [ignoredRepositories, setIgnoredRepositories] = useState({});
  const [uploading, setUploading] = useState(false);
  const [showIgnoredRepos, setShowIgnoredRepos] = useState(() => {
    // Load from localStorage on initial render
    const saved = localStorage.getItem('showIgnoredRepos');
    return saved === 'true';
  });
  const [syncSchedulerStatus, setSyncSchedulerStatus] = useState(null);

  useEffect(() => {
    fetchRepositories();
    fetchStreams();
    fetchRepositoryStreamMappings();
    fetchSyncSchedulerStatus();
  }, []);

  const fetchSyncSchedulerStatus = async () => {
    try {
      const response = await repositoryService.getSyncSchedulerStatus();
      // Handle both direct response and wrapped response
      const status = response.data || response;
      console.log('Sync scheduler status received:', status);
      // Extract the status fields (remove success if present)
      const { success, ...statusData } = status;
      console.log('Setting sync scheduler status:', statusData);
      setSyncSchedulerStatus(statusData);
    } catch (err) {
      console.error('Failed to fetch sync scheduler status:', err);
      // Set a default status object so UI still shows something
      setSyncSchedulerStatus({ isEnabled: true, schedule: '0 0 * * *' });
    }
  };

  const fetchStreams = async () => {
    try {
      const response = await repositoryService.getSnykStreams();
      setStreams(response.streams || []);
    } catch (err) {
      console.warn('Failed to fetch streams from Snyk mapping:', err);
      // Fallback to empty array if API fails
      setStreams([]);
    }
  };

  const fetchRepositoryStreamMappings = async () => {
    try {
      const response = await repositoryService.getRepositoryStreamMappings();
      setRepositoryStreamMappings(response.mappings || {});
      setIgnoredRepositories(response.ignored || {});
      console.log('Ignored repositories:', response.ignored || {});
      console.log('Total ignored repos:', Object.keys(response.ignored || {}).length);
    } catch (err) {
      console.warn('Failed to fetch repository-stream mappings:', err);
      setRepositoryStreamMappings({});
      setIgnoredRepositories({});
    }
  };

  const fetchRepositories = async () => {
    try {
      setLoading(true);
      const [reposData, snykCounts] = await Promise.all([
        repositoryService.getRepositories(),
        repositoryService.getSnykVulnerabilityCounts().catch(err => {
          console.warn('Failed to fetch Snyk vulnerability counts:', err);
          return {};
        })
      ]);
      setRepositories(reposData);
      setSnykVulnerabilityCounts(snykCounts);
      setError(null);
    } catch (err) {
      setError('Failed to fetch repositories');
      console.error('Error fetching repositories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRepository = () => {
    setEditingRepo(null);
    setFormData({
      githubRepoName: '',
      sonarProjectName: '',
      sonarBranch: 'develop',
      type: 'modern',
      owner: '',
      stream: '',
      drill4JAppId: '',
      githubWorkflowFileName: 'create-tag-and-jar-for-release.yml',
      serviceName: '',
      em: '',
      ignore: false,
      deploymentConfig: {
        adoPipelineId: 2048,
        serviceName: '',
        deploymentRegion: 'US',
        location: 'Primary',
        backupRequired: 'No',
        deploymentWithAppconfig: 'Yes'
      }
    });
    setOpenDialog(true);
  };

  const handleEditRepository = (repo) => {
    setEditingRepo(repo);
    // Get the actual stream value - prefer repo.stream, fallback to mapping
    const actualStream = repo.stream || getStreamForRepository(repo) || '';
    setFormData({
      githubRepoName: repo.githubRepoName || '',
      sonarProjectName: repo.sonarProjectName || repo.githubRepoName || '',
      sonarBranch: repo.sonarBranch || 'develop',
      type: repo.type || 'modern',
      owner: repo.owner || '',
      stream: actualStream,
      drill4JAppId: repo.drill4JAppId || '',
      githubWorkflowFileName: repo.githubWorkflowFileName || 'create-tag-and-jar-for-release.yml',
      serviceName: repo.serviceName || '',
      em: repo.em || '',
      ignore: repo.ignore || false,
      deploymentConfig: {
        adoPipelineId: repo.deploymentConfig?.adoPipelineId || 2048,
        serviceName: repo.deploymentConfig?.serviceName || '',
        deploymentRegion: repo.deploymentConfig?.deploymentRegion || 'US',
        location: repo.deploymentConfig?.location || 'Primary',
        backupRequired: repo.deploymentConfig?.backupRequired || 'No',
        deploymentWithAppconfig: repo.deploymentConfig?.deploymentWithAppconfig || 'Yes'
      }
    });
    setOpenDialog(true);
  };

  const handleDeleteRepository = async (repoId) => {
    if (!isAuthenticated() || !isAdmin()) {
      setError('You must be logged in as an admin to delete repositories');
      return;
    }

    if (window.confirm('Are you sure you want to delete this repository?')) {
      try {
        await repositoryService.deleteRepository(repoId);
        fetchRepositories();
      } catch (err) {
        setError('Failed to delete repository');
        console.error('Error deleting repository:', err);
      }
    }
  };

  const handleSyncRepository = async (repoId) => {
    try {
      await repositoryService.syncRepository(repoId);
      // Refresh repositories, Snyk counts, and stream mappings
      const [reposData, snykCounts, streamMappingsResponse] = await Promise.all([
        repositoryService.getRepositories(),
        repositoryService.getSnykVulnerabilityCounts().catch(err => {
          console.warn('Failed to fetch Snyk vulnerability counts:', err);
          return {};
        }),
        repositoryService.getRepositoryStreamMappings().catch(err => {
          console.warn('Failed to fetch repository-stream mappings:', err);
          return { mappings: {}, ignored: {} };
        })
      ]);
      setRepositories(reposData);
      setSnykVulnerabilityCounts(snykCounts);
      setRepositoryStreamMappings(streamMappingsResponse.mappings || {});
      setIgnoredRepositories(streamMappingsResponse.ignored || {});
    } catch (err) {
      setError('Failed to sync repository');
      console.error('Error syncing repository:', err);
    }
  };

  const handleBulkSync = async () => {
    try {
      setBulkSyncing(true);
      setError(null);
      setSuccessMessage(null);
      
      // Perform bulk sync
      let result;
      try {
        result = await repositoryService.bulkSyncRepositories();
        // Show success message with results
        const message = `Bulk sync completed! ${result.successful} successful, ${result.failed} failed out of ${result.total} repositories.`;
        setSuccessMessage(message);
      } catch (syncErr) {
        setError('Failed to perform bulk sync: ' + (syncErr.response?.data?.message || syncErr.message));
        console.error('Error during bulk sync:', syncErr);
        return;
      }
      
      // Refresh repositories, Snyk counts, and stream mappings to show updated data
      try {
        const [reposData, snykCounts, streamMappingsResponse] = await Promise.all([
          repositoryService.getRepositories(),
          repositoryService.getSnykVulnerabilityCounts().catch(err => {
            console.warn('Failed to fetch Snyk vulnerability counts:', err);
            return {};
          }),
          repositoryService.getRepositoryStreamMappings().catch(err => {
            console.warn('Failed to fetch repository-stream mappings:', err);
            return { mappings: {}, ignored: {} };
          })
        ]);
        setRepositories(reposData);
        setSnykVulnerabilityCounts(snykCounts);
        setRepositoryStreamMappings(streamMappingsResponse.mappings || {});
        setIgnoredRepositories(streamMappingsResponse.ignored || {});
      } catch (fetchErr) {
        // Don't override success message if bulk sync succeeded
        console.error('Error fetching repositories after bulk sync:', fetchErr);
        setError('Bulk sync completed, but failed to refresh repository list: ' + (fetchErr.response?.data?.message || fetchErr.message));
      }
      
      // Refresh sync scheduler status after bulk sync
      await fetchSyncSchedulerStatus();
      
      console.log('Bulk sync results:', result);
    } catch (err) {
      setError('Failed to perform bulk sync: ' + (err.response?.data?.message || err.message));
      console.error('Error during bulk sync:', err);
    } finally {
      setBulkSyncing(false);
    }
  };

  // Handle JSON file upload
  const handleJsonUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      setError('Please upload a JSON file');
      return;
    }

    if (!isAuthenticated() || !isAdmin()) {
      setError('You must be logged in as an admin to import repositories');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccessMessage(null);

      const fileContent = await file.text();
      const jsonData = JSON.parse(fileContent);

      if (!Array.isArray(jsonData)) {
        setError('JSON file must contain an array of objects');
        return;
      }

      const response = await authPost('/api/snyk-mapping/upload-json', {
        data: jsonData,
        clearExisting: true
      });

      if (response.success) {
        setSuccessMessage(`Successfully imported ${response.summary.success} repositories`);
        await fetchRepositories();
      } else {
        setError(response.error || 'Failed to import repositories');
      }
    } catch (err) {
      console.error('Error uploading JSON:', err);
      setError(err.response?.data?.error || 'Failed to upload JSON file');
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Handle JSON download
  const handleDownloadJson = async () => {
    if (!isAuthenticated() || !isAdmin()) {
      setError('You must be logged in as an admin to export repositories');
      return;
    }

    try {
      setError(null);
      const response = await authGet('/api/snyk-mapping/mappings');
      const mappings = response.mappings || [];

      if (mappings.length === 0) {
        setError('No repositories to export');
        return;
      }

      // Transform to export format
      const exportData = mappings.map(mapping => {
        const exportItem = {
          Project: mapping.Project || mapping.project || '',
          Repo: mapping.Repo || mapping.repo || '',
          Team: mapping.Team || mapping.team || '',
          EM: mapping.EM || mapping.em || '',
          Owner: mapping.Owner || mapping.owner || '',
          Stream: mapping.Stream || mapping.stream || '',
          reviewers: mapping.reviewers || [],
          drill4JAppId: mapping.drill4JAppId || '',
          type: mapping.type || 'modern',
          sonarProjectName: mapping.sonarProjectName || '',
          syncStatus: mapping.syncStatus || 'not_synced',
          ignore: mapping.ignore || false
        };
        return exportItem;
      });

      // Create and download file
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `repositories-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccessMessage('Repositories exported successfully');
    } catch (err) {
      console.error('Error downloading JSON:', err);
      setError('Failed to download repositories JSON');
    }
  };

  const handleSubmit = async () => {
    if (!isAuthenticated() || !isAdmin()) {
      setError('You must be logged in as an admin to save repositories');
      return;
    }

    try {
      if (editingRepo) {
        await repositoryService.updateRepository(editingRepo._id, formData);
        setOpenDialog(false);
        // Refresh data in place so user stays on same page/tab
        await Promise.all([
          fetchRepositories(),
          fetchRepositoryStreamMappings()
        ]);
      } else {
        await repositoryService.createRepository(formData);
        setOpenDialog(false);
        // Refresh both repositories and stream mappings for new repos
        await Promise.all([
          fetchRepositories(),
          fetchRepositoryStreamMappings()
        ]);
      }
    } catch (err) {
      setError('Failed to save repository');
      console.error('Error saving repository:', err);
    }
  };

  const getComplianceStatus = (repo) => {
    if (!repo.githubData || !repo.sonarData) return 'unknown';
    
    const hasProtection = repo.githubData.branchProtection?.enabled;
    const hasCoverage = repo.sonarData.coverage > 0;
    
    if (hasProtection && hasCoverage && repo.sonarData.coverage >= 80) return 'good';
    if (hasProtection || hasCoverage) return 'warning';
    return 'error';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'good': return <CheckCircleIcon color="success" />;
      case 'warning': return <WarningIcon color="warning" />;
      case 'error': return <ErrorIcon color="error" />;
      default: return <CircularProgress size={20} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'good': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleProtectionClick = (repo) => {
    setSelectedRepo(repo);
    setProtectionDialogOpen(true);
  };

  const getGitHubUrl = (repo) => {
    const githubOrg = process.env.REACT_APP_GITHUB_ORG || 'tr';
    const fullRepoName = repo.githubRepoName.includes('/') 
      ? repo.githubRepoName 
      : `${githubOrg}/${repo.githubRepoName}`;
    return `https://github.com/${fullRepoName}`;
  };

  const getSonarQubeUrl = (repo) => {
    const sonarUrl = process.env.REACT_APP_SONAR_URL || 'https://sonar.prod.thomsonreuters.com';
    return `${sonarUrl}/dashboard?id=${repo.sonarProjectName}`;
  };

  const getBranchProtectionUrl = (repo) => {
    const githubOrg = process.env.REACT_APP_GITHUB_ORG || 'tr';
    const fullRepoName = repo.githubRepoName.includes('/') 
      ? repo.githubRepoName 
      : `${githubOrg}/${repo.githubRepoName}`;
    const branch = repo.githubData?.branchProtection?.branch || 'main';
    return `https://github.com/${fullRepoName}/settings/branch_protection_rules`;
  };

  const getCoverageColor = (coverage) => {
    if (coverage >= 85) return '#4caf50'; // Green
    if (coverage >= 60) return '#ff9800'; // Amber
    return '#f44336'; // Red
  };

  const getCoverageBackgroundColor = (coverage) => {
    if (coverage >= 85) return '#e8f5e8'; // Light green
    if (coverage >= 60) return '#fff3e0'; // Light amber
    return '#ffebee'; // Light red
  };

  const getRatingColor = (rating) => {
    if (!rating) return 'default';
    // SonarQube ratings: A=1 (best), B=2, C=3, D=4, E=5 (worst)
    const ratingValue = parseInt(rating);
    if (ratingValue === 1) return 'success';
    if (ratingValue === 2) return 'info';
    if (ratingValue === 3) return 'warning';
    if (ratingValue >= 4) return 'error';
    return 'default';
  };

  const formatRating = (rating) => {
    if (!rating) return 'N/A';
    const ratingValue = parseInt(rating);
    const ratingLetter = ['A', 'B', 'C', 'D', 'E'][ratingValue - 1] || rating;
    return ratingLetter;
  };

  const getCardBackgroundColor = (repo) => {
    // If repository is ignored, return gray background
    if (isRepositoryIgnored(repo)) {
      return '#e0e0e0'; // Light gray for ignored repos
    }
    const overallCoverage = repo.sonarData?.coverage || 0;
    return getCoverageBackgroundColor(overallCoverage);
  };

  // Helper function to normalize repository name for matching
  const normalizeRepoName = (repoName) => {
    if (!repoName) return '';
    return repoName.toLowerCase().replace(/^tr\//, '').replace(/\/$/, '');
  };

  // Helper function to check if repository is ignored
  const isRepositoryIgnored = (repo) => {
    // First check if repo has ignore property set to true
    if (repo.ignore === true) {
      return true;
    }
    
    const repoName = repo.githubRepoName;
    const normalized = normalizeRepoName(repoName);
    
    // Check if repository is in ignored map
    if (ignoredRepositories[normalized] || ignoredRepositories[repoName]) {
      return true;
    }
    
    // Try with tr/ prefix
    const withPrefix = `tr/${normalized}`;
    if (ignoredRepositories[withPrefix]) {
      return true;
    }
    
    return false;
  };

  // Helper function to get stream for a repository from Snyk mapping
  const getStreamForRepository = (repo) => {
    // If repository is ignored, return null (will be grouped separately)
    if (isRepositoryIgnored(repo)) {
      return null;
    }
    
    const repoName = repo.githubRepoName;
    const normalized = normalizeRepoName(repoName);
    
    // Try to find stream in mappings
    if (repositoryStreamMappings[normalized]) {
      return repositoryStreamMappings[normalized];
    }
    
    // Try with original name
    if (repositoryStreamMappings[repoName]) {
      return repositoryStreamMappings[repoName];
    }
    
    // Try with tr/ prefix
    const withPrefix = `tr/${normalized}`;
    if (repositoryStreamMappings[withPrefix]) {
      return repositoryStreamMappings[withPrefix];
    }
    
    // Fallback to repository's stream field if available
    return repo.stream || null;
  };

  // Group repositories by stream dynamically based on available streams
  const repositoriesByStream = {};
  const allStreams = [...streams];
  
  // Separate ignored repositories
  const ignoredRepos = repositories.filter(repo => isRepositoryIgnored(repo));
  const nonIgnoredRepos = repositories.filter(repo => !isRepositoryIgnored(repo));
  
  // Debug logging
  if (ignoredRepos.length > 0) {
    console.log('Found ignored repositories:', ignoredRepos.map(r => r.githubRepoName));
  }
  
  // Use a Set to track which repositories have been assigned to a stream
  const assignedRepoIds = new Set();
  
  streams.forEach(stream => {
    repositoriesByStream[stream] = nonIgnoredRepos.filter(repo => {
      // Skip if already assigned to another stream
      if (assignedRepoIds.has(repo._id)) {
        return false;
      }
      const repoStream = getStreamForRepository(repo);
      if (repoStream === stream) {
        assignedRepoIds.add(repo._id);
        return true;
      }
      return false;
    });
  });
  
  // Also include repositories that don't match any stream in an "Unassigned" group
  // Only include repos that haven't been assigned to any stream yet
  const unassignedRepos = nonIgnoredRepos.filter(repo => {
    if (assignedRepoIds.has(repo._id)) {
      return false; // Already assigned to a stream
    }
    const repoStream = getStreamForRepository(repo);
    return !repoStream || !streams.includes(repoStream);
  });
  if (unassignedRepos.length > 0) {
    repositoriesByStream['Unassigned'] = unassignedRepos;
    if (!allStreams.includes('Unassigned')) {
      allStreams.push('Unassigned');
    }
  }
  
  // Add ignored repositories at the end
  if (ignoredRepos.length > 0) {
    repositoriesByStream['Ignored'] = ignoredRepos;
  }

  // Initialize expanded state for all streams (default to expanded)
  useEffect(() => {
    const streamKeys = Object.keys(repositoriesByStream);
    if (streamKeys.length === 0) return;
    
    setExpandedStreams(prev => {
      const newExpandedState = { ...prev };
      let hasChanges = false;
      streamKeys.forEach(stream => {
        if (!(stream in newExpandedState)) {
          newExpandedState[stream] = true; // Default to expanded
          hasChanges = true;
        }
      });
      return hasChanges ? newExpandedState : prev;
    });
  }, [streams, repositories.length]); // Use streams and repositories.length as dependencies

  const toggleStreamExpansion = (stream) => {
    setExpandedStreams(prev => ({
      ...prev,
      [stream]: !prev[stream]
    }));
  };

  const CoverageDisplay = ({ repo, showNewCode = false }) => {
    const coverage = repo.sonarData?.coverage;
    const newCodeCoverage = repo.sonarData?.newCodeCoverage;
    const lastExecuted = repo.sonarData?.lastExecuted;
    
    const displayCoverage = showNewCode ? newCodeCoverage : coverage;
    const label = showNewCode ? 'New Code Coverage' : 'Coverage';
    
    if (!displayCoverage && displayCoverage !== 0) {
      return <Typography variant="body2">{label}: N/A</Typography>;
    }

    const tooltipContent = (
      <Box>
        <Typography variant="body2">
          <strong>Overall Coverage:</strong> {coverage || 0}%
        </Typography>
        <Typography variant="body2">
          <strong>New Code Coverage:</strong> {newCodeCoverage || 0}%
        </Typography>
        <Typography variant="body2">
          <strong>Last Executed:</strong> {formatDate(lastExecuted)}
        </Typography>
        {repo.sonarData?.qualityGate && (
          <Typography variant="body2">
            <strong>Quality Gate:</strong> {repo.sonarData.qualityGate}
          </Typography>
        )}
      </Box>
    );

    return (
      <Tooltip title={tooltipContent} arrow placement="top">
        <Typography 
          variant="body2" 
          sx={{ 
            cursor: 'help',
            color: getCoverageColor(displayCoverage),
            fontWeight: 'bold'
          }}
        >
          {label}: {displayCoverage}%
        </Typography>
      </Tooltip>
    );
  };

  const BranchProtectionDisplay = ({ repo }) => {
    const protection = repo.githubData?.branchProtection;
    const isEnabled = protection?.enabled;

    if (!isEnabled) {
      return (
        <Typography variant="body2" color="textSecondary">
          Disabled
        </Typography>
      );
    }

    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Typography 
          variant="body2" 
          component="span"
          sx={{ 
            color: 'primary.main', 
            cursor: 'pointer',
            textDecoration: 'underline',
            '&:hover': {
              textDecoration: 'none'
            }
          }}
          onClick={() => handleProtectionClick(repo)}
        >
          Enabled
        </Typography>
        <LinkIcon fontSize="small" color="primary" />
      </Box>
    );
  };

  const BranchProtectionDialog = () => {
    if (!selectedRepo) return null;

    const protection = selectedRepo.githubData?.branchProtection;
    const githubUrl = getBranchProtectionUrl(selectedRepo);

    return (
      <Dialog 
        open={protectionDialogOpen} 
        onClose={() => setProtectionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SecurityIcon />
            Branch Protection Rules - {selectedRepo.githubRepoName}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Protection Settings
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  {protection?.enabled ? 
                    <CheckCircleIcon color="success" /> : 
                    <ErrorIcon color="error" />
                  }
                  <Typography>
                    <strong>Status:</strong> {protection?.enabled ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography mb={2}>
                  <strong>Branch:</strong> {protection?.branch || 'main'}
                </Typography>
              </Grid>

              {protection?.enabled && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Review Requirements
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      {protection?.requiredReviews > 0 ? 
                        <CheckCircleIcon color="success" fontSize="small" /> : 
                        <ErrorIcon color="error" fontSize="small" />
                      }
                      <Typography>
                        Required Reviews: {protection?.requiredReviews || 0}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      {protection?.dismissStaleReviews ? 
                        <CheckCircleIcon color="success" fontSize="small" /> : 
                        <ErrorIcon color="error" fontSize="small" />
                      }
                      <Typography>
                        Dismiss Stale Reviews: {protection?.dismissStaleReviews ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      {protection?.requireCodeOwnerReviews ? 
                        <CheckCircleIcon color="success" fontSize="small" /> : 
                        <ErrorIcon color="error" fontSize="small" />
                      }
                      <Typography>
                        Require Code Owner Reviews: {protection?.requireCodeOwnerReviews ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      {protection?.enforceAdmins ? 
                        <CheckCircleIcon color="success" fontSize="small" /> : 
                        <ErrorIcon color="error" fontSize="small" />
                      }
                      <Typography>
                        Enforce for Admins: {protection?.enforceAdmins ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      {protection?.restrictPushes ? 
                        <CheckCircleIcon color="success" fontSize="small" /> : 
                        <ErrorIcon color="error" fontSize="small" />
                      }
                      <Typography>
                        Restrict Pushes: {protection?.restrictPushes ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                  </Grid>

                  {protection?.requiredStatusChecks && protection.requiredStatusChecks.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Required Status Checks
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {protection.requiredStatusChecks.map((check, index) => (
                          <Chip 
                            key={index} 
                            label={check} 
                            size="small" 
                            color="primary"
                            variant="outlined" 
                          />
                        ))}
                      </Box>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => window.open(githubUrl, '_blank')}
            startIcon={<GitHubIcon />}
            variant="outlined"
          >
            View on GitHub
          </Button>
          <Button onClick={() => setProtectionDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const RepositoryCard = ({ repo }) => {
    const counts = snykVulnerabilityCounts[repo._id] || { total: 0, commonLibrary: 0 };
    const coverage = repo.sonarData?.coverage || 0;
    const coverageColors = getCoverageChipColors(coverage);
    
    return (
      <Card sx={{ 
        backgroundColor: getCardBackgroundColor(repo),
        border: '1px solid',
        borderColor: getCoverageColor(coverage),
        borderWidth: '2px',
        width: '100%',
        height: 'auto',
        position: 'relative'
      }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          {/* Repository Name and Stream */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} gap={1}>
            <Box flex={1} minWidth={0}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.85rem', 
                  lineHeight: 1.2, 
                  fontWeight: 'bold',
                  mb: 0.5,
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  hyphens: 'auto'
                }}
              >
                {repo.githubRepoName}
              </Typography>
              <Chip 
                label={getStreamForRepository(repo) || 'Unassigned'} 
                size="small" 
                color="primary" 
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: '18px' }}
              />
            </Box>
            <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
              <Tooltip title="Quality Gate Threshold" arrow>
                <Chip
                  label={`${repo.sonarData?.qualityGateThreshold || 0}%`}
                  size="small"
                  sx={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    fontWeight: 'bold',
                    fontSize: '0.65rem',
                    height: '18px'
                  }}
                />
              </Tooltip>
              {isAdmin() && (
                <IconButton
                  size="small"
                  onClick={() => handleEditRepository(repo)}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 1)'
                    },
                    padding: '2px',
                    minWidth: '24px',
                    width: '24px',
                    height: '24px',
                    flexShrink: 0
                  }}
                >
                  <EditIcon sx={{ fontSize: '14px' }} />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Metrics in compact grid */}
          <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
            {/* Coverage */}
            <Tooltip 
              title={`Overall Coverage: ${coverage}%`} 
              arrow
            >
              <Chip
                label={`${coverage}%`}
                size="small"
                sx={{
                  backgroundColor: coverageColors.backgroundColor,
                  color: coverageColors.color,
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  height: '20px',
                  cursor: 'help'
                }}
              />
            </Tooltip>

            {/* Security Rating */}
            <Tooltip title="Security Rating" arrow>
              <Chip 
                label={formatRating(repo.sonarData?.securityRating)} 
                size="small" 
                color={getRatingColor(repo.sonarData?.securityRating)}
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: '20px', cursor: 'help' }}
              />
            </Tooltip>

            {/* Reliability Rating */}
            <Tooltip title="Reliability Rating" arrow>
              <Chip 
                label={formatRating(repo.sonarData?.reliabilityRating)} 
                size="small" 
                color={getRatingColor(repo.sonarData?.reliabilityRating)}
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: '20px', cursor: 'help' }}
              />
            </Tooltip>

            {/* Snyk Vulnerabilities */}
            <Tooltip 
              title={`Snyk Vulnerabilities: ${counts.total} total${counts.commonLibrary > 0 ? `, ${counts.commonLibrary} covered by common library` : ''}`} 
              arrow
            >
              <Chip
                label={`${counts.total}${counts.commonLibrary > 0 ? ` (${counts.commonLibrary})` : ''}`}
                size="small"
                variant="outlined"
                sx={{ 
                  fontSize: '0.7rem', 
                  height: '20px',
                  cursor: 'help'
                }}
              />
            </Tooltip>

            {/* Protection */}
            <Tooltip 
              title={repo.githubData?.branchProtection?.enabled ? 'Branch Protection: Enabled' : 'Branch Protection: Disabled'} 
              arrow
            >
              <Box sx={{ cursor: 'help' }}>
                <BranchProtectionDisplay repo={repo} />
              </Box>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const getCoverageChipColors = (coverage) => {
    if (coverage >= 85) {
      return {
        backgroundColor: '#4caf50', // Green
        color: '#ffffff' // White text for good contrast
      };
    }
    if (coverage >= 60) {
      return {
        backgroundColor: '#ff9800', // Orange
        color: '#ffffff' // White text for good contrast
      };
    }
    return {
      backgroundColor: '#f44336', // Red
      color: '#ffffff' // White text for good contrast
    };
  };

  const CombinedCoverageDisplay = ({ repo }) => {
    const coverage = repo.sonarData?.coverage;
    const lastExecuted = repo.sonarData?.lastExecuted;

    const tooltipContent = (
      <Box>
        <Typography variant="body2">
          <strong>Overall Coverage:</strong> {coverage || 0}%
        </Typography>
        <Typography variant="body2">
          <strong>Last Executed:</strong> {formatDate(lastExecuted)}
        </Typography>
        {repo.sonarData?.qualityGate && (
          <Typography variant="body2">
            <strong>Quality Gate:</strong> {repo.sonarData.qualityGate}
          </Typography>
        )}
      </Box>
    );

    const overallColors = getCoverageChipColors(coverage || 0);

    return (
      <Tooltip title={tooltipContent} arrow placement="top">
        <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ cursor: 'help' }}>
          <Chip
            label={`${coverage || 0}%`}
            size="small"
            sx={{
              backgroundColor: overallColors.backgroundColor,
              color: overallColors.color,
              fontWeight: 'bold',
              fontSize: '0.75rem'
            }}
          />
        </Box>
      </Tooltip>
    );
  };

  const RepositoryTable = ({ repositories: repoList, title }) => {
    const isExpanded = expandedStreams[title] !== false; // Default to true if not set
    
    return (
      <Box mb={3}>
        <Box
          onClick={() => toggleStreamExpansion(title)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            p: 1.5,
            borderRadius: 1,
            backgroundColor: 'action.hover',
            '&:hover': {
              backgroundColor: 'action.selected'
            },
            mb: isExpanded ? 2 : 0
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {title} ({repoList.length})
          </Typography>
          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={isExpanded}>
          <TableContainer component={Paper}>
          <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '25%', fontWeight: 'bold' }}>Repository</TableCell>
                <TableCell sx={{ width: '10%', fontWeight: 'bold', textAlign: 'right' }}>Coverage</TableCell>
                <TableCell sx={{ width: '12%', fontWeight: 'bold', textAlign: 'center' }}>Security Rating</TableCell>
                <TableCell sx={{ width: '12%', fontWeight: 'bold', textAlign: 'center' }}>Reliability Rating</TableCell>
                <TableCell sx={{ width: '12%', fontWeight: 'bold', textAlign: 'right' }}>Snyk Vulnerabilities</TableCell>
                <TableCell sx={{ width: '12%', fontWeight: 'bold', textAlign: 'center' }}>Stream</TableCell>
                <TableCell sx={{ width: '17%', fontWeight: 'bold', textAlign: 'left' }}>Protection</TableCell>
                {isAdmin() && (
                  <TableCell sx={{ width: '5%', fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {repoList.map((repo) => {
                const isIgnored = isRepositoryIgnored(repo);
                return (
                  <TableRow 
                    key={repo._id}
                    sx={{
                      backgroundColor: isIgnored ? '#e0e0e0' : 'transparent',
                      '&:hover': {
                        backgroundColor: isIgnored ? '#d0d0d0' : 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                  <TableCell sx={{ width: '25%' }}>
                    <Box>
                      <Typography
                        component="a"
                        href={getGitHubUrl(repo)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontWeight: 'bold',
                          display: 'block',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {repo.githubRepoName}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <Typography
                          component="a"
                          href={getSonarQubeUrl(repo)}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            fontSize: '0.75rem',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          SonarQube →
                        </Typography>
                        <Chip
                          label={`${repo.sonarData?.qualityGateThreshold || 0}%`}
                          size="small"
                          sx={{
                            backgroundColor: '#fff3e0', // Light orange
                            color: '#f57c00',
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            height: '18px'
                          }}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '10%', textAlign: 'right' }}>
                    <Box display="flex" justifyContent="flex-end">
                      <CombinedCoverageDisplay repo={repo} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '12%', textAlign: 'center' }}>
                    <Box display="flex" justifyContent="center">
                      <Chip 
                        label={formatRating(repo.sonarData?.securityRating)} 
                        size="small" 
                        color={getRatingColor(repo.sonarData?.securityRating)}
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '12%', textAlign: 'center' }}>
                    <Box display="flex" justifyContent="center">
                      <Chip 
                        label={formatRating(repo.sonarData?.reliabilityRating)} 
                        size="small" 
                        color={getRatingColor(repo.sonarData?.reliabilityRating)}
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '12%', textAlign: 'right' }}>
                    {(() => {
                      const counts = snykVulnerabilityCounts[repo._id] || { total: 0, commonLibrary: 0 };
                      return (
                        <Typography variant="body2" sx={{ textAlign: 'right' }}>
                          {counts.total}
                          {counts.commonLibrary > 0 && (
                            <Typography component="span" color="textSecondary" sx={{ ml: 0.5 }}>
                              ({counts.commonLibrary})
                            </Typography>
                          )}
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ width: '12%', textAlign: 'center' }}>
                    <Box display="flex" justifyContent="center">
                      <Chip label={getStreamForRepository(repo) || 'Unassigned'} size="small" color="primary" variant="outlined" />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '17%', textAlign: 'left' }}>
                    <BranchProtectionDisplay repo={repo} />
                  </TableCell>
                  {isAdmin() && (
                    <TableCell sx={{ width: '5%', textAlign: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditRepository(repo)}
                        color="primary"
                        sx={{
                          padding: '4px',
                          minWidth: '28px',
                          width: '28px',
                          height: '28px'
                        }}
                      >
                        <EditIcon sx={{ fontSize: '16px' }} />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </Collapse>
      </Box>
    );
  };

  const RepositoryCardSection = ({ repositories: repoList, title }) => {
    const isExpanded = expandedStreams[title] !== false; // Default to true if not set
    
    return (
      <Box mb={3}>
        <Box
          onClick={() => toggleStreamExpansion(title)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            p: 1.5,
            borderRadius: 1,
            backgroundColor: 'action.hover',
            '&:hover': {
              backgroundColor: 'action.selected'
            },
            mb: isExpanded ? 2 : 0
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {title} ({repoList.length})
          </Typography>
          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={isExpanded}>
          <Box 
            sx={{ 
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              '& > *': {
                flex: '0 0 auto',
                width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 11px)', lg: 'calc(25% - 12px)', xl: 'calc(20% - 13px)' },
                maxWidth: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 11px)', lg: 'calc(25% - 12px)', xl: 'calc(20% - 13px)' }
              }
            }}
          >
            {repoList.map((repo) => (
              <RepositoryCard key={repo._id} repo={repo} />
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" gutterBottom>
            Repositories
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage and monitor GitHub repositories with quality metrics
          </Typography>
          <Box mt={1} display="flex" gap={2} flexWrap="wrap" alignItems="center">
            {syncSchedulerStatus ? (
              <>
                {syncSchedulerStatus.lastRun ? (
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                    Last Auto Sync: {new Date(syncSchedulerStatus.lastRun).toLocaleString()}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                    Last Auto Sync: Not yet run
                  </Typography>
                )}
                {syncSchedulerStatus.nextRun ? (
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                    Next Auto Sync: {new Date(syncSchedulerStatus.nextRun).toLocaleString()}
                  </Typography>
                ) : syncSchedulerStatus.isEnabled ? (
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                    Next Auto Sync: Scheduled (12 AM daily)
                  </Typography>
                ) : null}
              </>
            ) : (
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                Auto-sync: Loading...
              </Typography>
            )}
          </Box>
        </Box>
        {isAdmin() && (
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={bulkSyncing ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={handleBulkSync}
              disabled={bulkSyncing || loading}
            >
              {bulkSyncing ? 'Syncing All...' : 'Sync All'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<UploadIcon />}
              component="label"
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Import JSON'}
              <input
                type="file"
                accept=".json"
                hidden
                onChange={handleJsonUpload}
                disabled={uploading}
              />
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadJson}
              disabled={repositories.length === 0}
            >
              Export JSON
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddRepository}
            >
              New Repository
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Paper sx={{ width: '100%', mr: 2 }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Card View" />
            <Tab label="Table View" />
          </Tabs>
        </Paper>
        <FormControlLabel
          control={
            <Switch
              checked={showIgnoredRepos}
              onChange={async (e) => {
                const newValue = e.target.checked;
                // Save to localStorage
                localStorage.setItem('showIgnoredRepos', newValue.toString());
                setShowIgnoredRepos(newValue);
                // Refresh repositories and stream mappings to show changes
                await Promise.all([
                  fetchRepositories(),
                  fetchRepositoryStreamMappings()
                ]);
              }}
              color="primary"
            />
          }
          label="Show Ignored Repos"
          sx={{ whiteSpace: 'nowrap' }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {tabValue === 0 ? (
            <Box>
              {Object.keys(repositoriesByStream)
                .filter(stream => {
                  // Filter out Ignored stream if checkbox is unchecked
                  if (stream === 'Ignored') {
                    return showIgnoredRepos;
                  }
                  return true;
                })
                .map(stream => (
                  repositoriesByStream[stream] && repositoriesByStream[stream].length > 0 && (
                    <RepositoryCardSection 
                      key={stream} 
                      repositories={repositoriesByStream[stream]} 
                      title={stream} 
                    />
                  )
                ))}
            </Box>
          ) : (
            <Box>
              {Object.keys(repositoriesByStream)
                .filter(stream => {
                  // Filter out Ignored stream if checkbox is unchecked
                  if (stream === 'Ignored') {
                    return showIgnoredRepos;
                  }
                  return true;
                })
                .map(stream => (
                  repositoriesByStream[stream] && repositoriesByStream[stream].length > 0 && (
                    <RepositoryTable 
                      key={stream} 
                      repositories={repositoriesByStream[stream]} 
                      title={stream} 
                    />
                  )
                ))}
            </Box>
          )}
        </>
      )}

      {/* Add/Edit Repository Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRepo ? 'Edit Repository' : 'Add Repository'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="GitHub Repository Name"
              value={formData.githubRepoName}
              onChange={(e) => setFormData({ ...formData, githubRepoName: e.target.value })}
              margin="normal"
              required
              disabled={!!editingRepo}
            />
            <TextField
              fullWidth
              label="SonarQube Project Name"
              value={formData.sonarProjectName}
              onChange={(e) => setFormData({ ...formData, sonarProjectName: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="SonarQube Branch"
              value={formData.sonarBranch}
              onChange={(e) => setFormData({ ...formData, sonarBranch: e.target.value })}
              margin="normal"
              helperText="Branch to fetch SonarQube metrics from (default: develop)"
            />
            <TextField
              fullWidth
              label="Owner"
              value={formData.owner}
              onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="EM (Engineering Manager)"
              value={formData.em}
              onChange={(e) => setFormData({ ...formData, em: e.target.value })}
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Stream</InputLabel>
              <Select
                value={formData.stream}
                label="Stream"
                onChange={(e) => setFormData({ ...formData, stream: e.target.value })}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {streams.map((stream) => (
                  <MenuItem key={stream} value={stream}>
                    {stream}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                <MenuItem value="modern">Modern</MenuItem>
                <MenuItem value="legacy">Legacy</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Drill4J App ID"
              value={formData.drill4JAppId}
              onChange={(e) => setFormData({ ...formData, drill4JAppId: e.target.value })}
              margin="normal"
              helperText="Optional: Custom Drill4J application ID"
            />
            <TextField
              fullWidth
              label="GitHub Workflow File Name"
              value={formData.githubWorkflowFileName}
              onChange={(e) => setFormData({ ...formData, githubWorkflowFileName: e.target.value })}
              margin="normal"
              helperText="The .yml workflow file name (e.g., create-tag-and-jar-for-release.yml)"
            />
            <TextField
              fullWidth
              label="Service Name"
              value={formData.serviceName}
              onChange={(e) => setFormData({ ...formData, serviceName: e.target.value })}
              margin="normal"
              helperText="Name of the microservice this repository deploys (e.g., 'Dashboard Builder Service')"
            />

            {/* Deployment Configuration Section */}
            <Divider sx={{ my: 3 }}>
              <Chip label="Deployment Configuration" />
            </Divider>

            <TextField
              fullWidth
              label="ADO Pipeline ID"
              type="number"
              value={formData.deploymentConfig?.adoPipelineId || 2048}
              onChange={(e) => setFormData({
                ...formData,
                deploymentConfig: {
                  ...formData.deploymentConfig,
                  adoPipelineId: parseInt(e.target.value) || 2048
                }
              })}
              margin="normal"
              helperText="Azure DevOps Pipeline Definition ID (default: 2048)"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>ADO Service Name</InputLabel>
              <Select
                value={formData.deploymentConfig?.serviceName || ''}
                label="ADO Service Name"
                onChange={(e) => setFormData({
                  ...formData,
                  deploymentConfig: {
                    ...formData.deploymentConfig,
                    serviceName: e.target.value
                  }
                })}
              >
                <MenuItem value="">
                  <em>Select Service</em>
                </MenuItem>
                <MenuItem value="advanceai">advanceai</MenuItem>
                <MenuItem value="advanceaiui">advanceaiui</MenuItem>
                <MenuItem value="isheetversion">isheetversion</MenuItem>
                <MenuItem value="isheetconnector">isheetconnector</MenuItem>
                <MenuItem value="formsbuilder">formsbuilder</MenuItem>
                <MenuItem value="isheetdataimporter">isheetdataimporter</MenuItem>
                <MenuItem value="gsuite">gsuite</MenuItem>
                <MenuItem value="aieng">aieng</MenuItem>
                <MenuItem value="aimgr">aimgr</MenuItem>
                <MenuItem value="dashboard">dashboard</MenuItem>
                <MenuItem value="docauto">docauto</MenuItem>
                <MenuItem value="docusign">docusign</MenuItem>
                <MenuItem value="unifieddashboard">unifieddashboard</MenuItem>
                <MenuItem value="workflow">workflow</MenuItem>
                <MenuItem value="transcodingengine">transcodingengine</MenuItem>
                <MenuItem value="officeonline">officeonline</MenuItem>
                <MenuItem value="thirdpartyservices">thirdpartyservices</MenuItem>
                <MenuItem value="tenantservice">tenantservice</MenuItem>
                <MenuItem value="schedulingservice">schedulingservice</MenuItem>
                <MenuItem value="sitetemplatemarketplace">sitetemplatemarketplace</MenuItem>
                <MenuItem value="hybrid">hybrid</MenuItem>
                <MenuItem value="trsetup">trsetup</MenuItem>
                <MenuItem value="isheet">isheet</MenuItem>
                <MenuItem value="metadata">metadata</MenuItem>
                <MenuItem value="files">files</MenuItem>
                <MenuItem value="filesviewer">filesviewer</MenuItem>
                <MenuItem value="antivirusservice">antivirusservice</MenuItem>
                <MenuItem value="dashboardbuilderservice">dashboardbuilderservice</MenuItem>
                <MenuItem value="camundawrapper">camundawrapper</MenuItem>
                <MenuItem value="camundaengine">camundaengine</MenuItem>
                <MenuItem value="unifiedsearch">unifiedsearch</MenuItem>
                <MenuItem value="search">search</MenuItem>
                <MenuItem value="authserver">authserver</MenuItem>
                <MenuItem value="systemcache">systemcache</MenuItem>
                <MenuItem value="imahighqservice">imahighqservice</MenuItem>
                <MenuItem value="imaltservice">imaltservice</MenuItem>
                <MenuItem value="imaapp">imaapp</MenuItem>
                <MenuItem value="isheetuimodernization">isheetuimodernization</MenuItem>
                <MenuItem value="dealcloserintegration">dealcloserintegration</MenuItem>
                <MenuItem value="usersync">usersync</MenuItem>
                <MenuItem value="taskuimodernization">taskuimodernization</MenuItem>
                <MenuItem value="highqmcp">highqmcp</MenuItem>
                <MenuItem value="aimlmanager">aimlmanager</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Deployment Region</InputLabel>
              <Select
                value={formData.deploymentConfig?.deploymentRegion || 'US'}
                label="Deployment Region"
                onChange={(e) => setFormData({
                  ...formData,
                  deploymentConfig: {
                    ...formData.deploymentConfig,
                    deploymentRegion: e.target.value
                  }
                })}
              >
                <MenuItem value="US">US</MenuItem>
                <MenuItem value="EU">EU</MenuItem>
                <MenuItem value="APAC">APAC</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <Typography variant="body2" sx={{ mb: 1 }}>Location</Typography>
              <RadioGroup
                row
                value={formData.deploymentConfig?.location || 'Primary'}
                onChange={(e) => setFormData({
                  ...formData,
                  deploymentConfig: {
                    ...formData.deploymentConfig,
                    location: e.target.value
                  }
                })}
              >
                <FormControlLabel value="Primary" control={<Radio />} label="Primary" />
                <FormControlLabel value="DR" control={<Radio />} label="DR" />
              </RadioGroup>
            </FormControl>

            <Box display="flex" gap={2} sx={{ mt: 2 }}>
              <FormControl fullWidth>
                <Typography variant="body2" sx={{ mb: 1 }}>Backup Required?</Typography>
                <RadioGroup
                  row
                  value={formData.deploymentConfig?.backupRequired || 'No'}
                  onChange={(e) => setFormData({
                    ...formData,
                    deploymentConfig: {
                      ...formData.deploymentConfig,
                      backupRequired: e.target.value
                    }
                  })}
                >
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>

              <FormControl fullWidth>
                <Typography variant="body2" sx={{ mb: 1 }}>Deployment with Appconfig?</Typography>
                <RadioGroup
                  row
                  value={formData.deploymentConfig?.deploymentWithAppconfig || 'Yes'}
                  onChange={(e) => setFormData({
                    ...formData,
                    deploymentConfig: {
                      ...formData.deploymentConfig,
                      deploymentWithAppconfig: e.target.value
                    }
                  })}
                >
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Box>

            {editingRepo && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.ignore || false}
                    onChange={(e) => setFormData({ ...formData, ignore: e.target.checked })}
                    color="primary"
                  />
                }
                label="Ignore Repository"
                sx={{ mt: 2, mb: 1 }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingRepo ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Branch Protection Dialog */}
      <BranchProtectionDialog />
    </Box>
  );
};

export default RepositoriesView;
